import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Plus, Trash2, Edit2, MapPin, LogOut, BarChart3, Home,
  ChevronUp, ChevronDown, FileText, DollarSign, Clock, CheckCircle,
  ExternalLink, X, User, Upload, Building, Save, GripVertical,
  Menu, Mail, Lock, UserX, UserCheck, Eye, KeyRound, ChevronRight, Wallet, Users, History, AlertTriangle, Bell, Filter, MapPinOff,
  FileType, Copy, Download
} from 'lucide-react';
import { STATUS_ORDER, STATUS_COLORS } from '../utils/constants';
import Comptabilite from './Comptabilite';
import ClientsList from './ClientsList';
import AuditLog from './AuditLog';
import { ClientService } from '../services/clientService';
import { TemplateService, TEMPLATE_TYPES, TEMPLATE_TAGS, TEMPLATE_MAX_BYTES } from '../services/templateService';
import { AuditService, AUDIT_ACTIONS } from '../services/auditService';
import { NotifyService } from '../services/notifyService';
import { NotificationService } from '../services/notificationService';
import NotificationBell from './NotificationBell';
import {
  getPaymentStatus, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_STYLES, isProjectLate, getDaysSinceStatus,
  DEFAULT_DELAI_RETARD_JOURS, DEFAULT_TVA
} from '../utils/finance';
import AccountSettings from './AccountSettings';
import StatusBadge from './ui/StatusBadge';
import Map from './Map';
import ProjectForm from './ProjectForm';
import ProjectDetails from './ProjectDetails';
import Statistics from './Statistics';
import {
  DEFAULT_CONFIG, NATURES_PROJETS, ROLES, ROLE_LABELS, FULL_ACCESS_ROLES
} from '../utils/constants';
import { ProjectService, AgentService, MemberService } from '../services/firebaseService';
import { AuthService } from '../services/authService';
import {
  formatNumber, calculateResteAPayer, formatRegimeDisplay,
  searchProjects, sortProjects, generateRefGlobal, generateNumeroDossier,
  debounce
} from '../utils/helpers';
import { merchichConverter } from '../services/merchichService';

// Profile Management Modal Component
const ProfileManagement = ({ userId, onClose, isOwner = true }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [agents, setAgents] = useState([]);
  const [members, setMembers] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [memberForm, setMemberForm] = useState({ agentName: '', email: '', password: '', role: ROLES.TECHNICIEN });
  const [creatingMember, setCreatingMember] = useState(false);
  const [templates, setTemplates] = useState({});
  const [templateBusy, setTemplateBusy] = useState('');
  const [copiedTag, setCopiedTag] = useState('');
  const [newProjectType, setNewProjectType] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [draggedTypeIndex, setDraggedTypeIndex] = useState(null);

  const [profile, setProfile] = useState({
    nomArchitecte: '',
    nomSociete: '',
    ice: '',
    patente: '',
    rc: '',
    identifiantFiscal: '',
    siegeSocial: '',
    rib: '',
    banque: '',
    email: '',
    mobile: '',
    fixe: '',
    fax: '',
    logoUrl: '',
    logoColor: '#3B82F6',
    delaiRetardJours: DEFAULT_DELAI_RETARD_JOURS,
    webhookUrl: '',
    tauxTVA: DEFAULT_TVA
  });
  const [webhookTest, setWebhookTest] = useState('');

  const loadMembers = useCallback(async () => {
    const result = await MemberService.getMembers(userId);
    if (result.success) {
      setMembers(result.members);
    }
  }, [userId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadMembers();
      TemplateService.getAll(userId).then(setTemplates);
      const result = await AgentService.getUserData(userId);
      if (result.success && result.data) {
        setAgents(result.data.agents || []);
        // Load project types or use defaults
        setProjectTypes(result.data.projectTypes || [...NATURES_PROJETS]);
        setProfile(prev => ({
          ...prev,
          ...result.data.profile
        }));
        if (result.data.profile?.logoUrl) {
          setLogoPreview(result.data.profile.logoUrl);
        }
      } else {
        // No data yet, use default project types
        setProjectTypes([...NATURES_PROJETS]);
      }
      setLoading(false);
    };
    loadData();
  }, [userId, loadMembers]);

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Le logo ne doit pas dépasser 2 Mo');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        // Extract dominant color from image
        extractDominantColor(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const extractDominantColor = (dataUrl) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 50;
      canvas.height = 50;
      ctx.drawImage(img, 0, 0, 50, 50);

      const imageData = ctx.getImageData(0, 0, 50, 50).data;
      let r = 0, g = 0, b = 0, count = 0;

      for (let i = 0; i < imageData.length; i += 4) {
        // Skip transparent and very light/dark pixels
        if (imageData[i + 3] > 128) {
          const brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          if (brightness > 30 && brightness < 225) {
            r += imageData[i];
            g += imageData[i + 1];
            b += imageData[i + 2];
            count++;
          }
        }
      }

      if (count > 0) {
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);
        const hex = '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
        handleProfileChange('logoColor', hex);
      }
    };
    img.src = dataUrl;
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let logoUrl = profile.logoUrl;

      // If there's a new logo file, convert to base64 and store
      if (logoFile) {
        logoUrl = logoPreview; // Use the base64 data URL directly
      }

      const result = await AgentService.saveUserData(userId, {
        profile: {
          ...profile,
          logoUrl
        },
        agents,
        projectTypes
      });

      if (result.success) {
        setSuccess('Profil sauvegardé avec succès');
        setLogoFile(null);
        AuditService.log(AUDIT_ACTIONS.PROFILE_UPDATE, { entityType: 'profil', label: profile.nomSociete || profile.nomArchitecte || '', details: { webhookUrl: profile.webhookUrl || '' } });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
    }

    setSaving(false);
  };

  const handleAddProjectType = async () => {
    if (!newProjectType.trim()) {
      setError('Le type de projet est requis');
      return;
    }

    if (projectTypes.includes(newProjectType.trim())) {
      setError('Ce type de projet existe déjà');
      return;
    }

    const updatedTypes = [...projectTypes, newProjectType.trim()];
    setProjectTypes(updatedTypes);
    setNewProjectType('');
    setError('');

    // Save to Firebase immediately
    await AgentService.saveUserData(userId, {
      projectTypes: updatedTypes
    });
  };

  const handleDeleteProjectType = async (type) => {
    if (!window.confirm(`Supprimer le type "${type}"?`)) return;

    const updatedTypes = projectTypes.filter(t => t !== type);
    setProjectTypes(updatedTypes);

    // Save to Firebase immediately
    await AgentService.saveUserData(userId, {
      projectTypes: updatedTypes
    });
  };

  const handleDragStart = (e, index) => {
    setDraggedTypeIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTypeIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedTypeIndex === null || draggedTypeIndex === dropIndex) return;

    const newTypes = [...projectTypes];
    const [draggedItem] = newTypes.splice(draggedTypeIndex, 1);
    newTypes.splice(dropIndex, 0, draggedItem);

    setProjectTypes(newTypes);
    setDraggedTypeIndex(null);

    // Save to Firebase immediately
    await AgentService.saveUserData(userId, {
      projectTypes: newTypes
    });
  };

  const handleMemberFormChange = (field, value) => {
    setMemberForm(prev => ({ ...prev, [field]: value }));
  };

  // Créer un compte pour un agent (email + mot de passe + rôle)
  const handleCreateMember = async () => {
    setError('');
    setSuccess('');

    const agentName = memberForm.agentName.trim();
    const email = memberForm.email.trim().toLowerCase();
    const password = memberForm.password;

    if (!agentName) {
      setError("Le nom de l'agent est requis");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Un email valide est requis');
      return;
    }
    if (!password || password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (members.some(m => m.isActive !== false && m.agentName === agentName)) {
      setError('Un agent actif porte déjà ce nom');
      return;
    }

    setCreatingMember(true);
    const result = await AuthService.createMemberAccount(userId, {
      email,
      password,
      agentName,
      role: memberForm.role
    });

    if (result.success) {
      // Le nom reste utilisé comme "Agent Responsable" dans les dossiers
      if (!agents.includes(agentName)) {
        const addResult = await AgentService.addAgent(userId, agentName);
        if (addResult.success) {
          setAgents(addResult.agents);
        }
      }
      await loadMembers();
      setMemberForm({ agentName: '', email: '', password: '', role: ROLES.TECHNICIEN });
      setSuccess(`Compte créé pour ${agentName} (${email})`);
      AuditService.log(AUDIT_ACTIONS.MEMBER_CREATE, { entityType: 'agent', entityId: result.uid, label: `${agentName} · ${email} · ${ROLE_LABELS[memberForm.role]}` });
    } else {
      setError(result.error);
    }
    setCreatingMember(false);
  };

  // Désactiver un membre : il ne peut plus se connecter ni lire les données
  const handleDeactivateMember = async (member) => {
    if (!window.confirm(`Désactiver le compte de "${member.agentName}" ?\nIl ne pourra plus se connecter.`)) return;

    setError('');
    const result = await MemberService.setMemberActive(member.id, false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    AuditService.log(AUDIT_ACTIONS.MEMBER_DEACTIVATE, { entityType: 'agent', entityId: member.id, label: member.agentName });
    const otherActive = members.some(m => m.id !== member.id && m.isActive !== false && m.agentName === member.agentName);
    if (!otherActive && agents.includes(member.agentName)) {
      const delResult = await AgentService.deleteAgent(userId, member.agentName);
      if (delResult.success) {
        setAgents(delResult.agents);
      }
    }
    await loadMembers();
  };

  const handleReactivateMember = async (member) => {
    setError('');
    const result = await MemberService.setMemberActive(member.id, true);
    if (!result.success) {
      setError(result.error);
      return;
    }
    if (!agents.includes(member.agentName)) {
      const addResult = await AgentService.addAgent(userId, member.agentName);
      if (addResult.success) {
        setAgents(addResult.agents);
      }
    }
    AuditService.log(AUDIT_ACTIONS.MEMBER_REACTIVATE, { entityType: 'agent', entityId: member.id, label: member.agentName });
    await loadMembers();
  };

  const handleChangeMemberRole = async (member, role) => {
    setError('');
    const result = await MemberService.setMemberRole(member.id, role);
    if (!result.success) {
      setError(result.error);
      return;
    }
    AuditService.log(AUDIT_ACTIONS.MEMBER_ROLE, { entityType: 'agent', entityId: member.id, label: `${member.agentName} → ${ROLE_LABELS[role]}` });
    await loadMembers();
  };

  // Supprimer un agent "nom seul" (sans compte)
  const handleDeleteLegacyAgent = async (agentName) => {
    if (!window.confirm(`Supprimer l'agent "${agentName}"?`)) return;

    const result = await AgentService.deleteAgent(userId, agentName);
    if (result.success) {
      setAgents(result.agents);
    } else {
      setError(result.error);
    }
  };

  // Modèles Word (devis / facture)
  const handleTemplateUpload = async (type, file) => {
    if (!file) return;
    setError('');
    setSuccess('');
    setTemplateBusy(type);
    const r = await TemplateService.save(userId, type, file);
    setTemplateBusy('');
    if (r.success) {
      setTemplates(await TemplateService.getAll(userId));
      setSuccess(`Modèle ${type} enregistré : ${file.name}`);
      AuditService.log(AUDIT_ACTIONS.PROFILE_UPDATE, { entityType: 'modele', label: `Modèle ${type} : ${file.name}` });
    } else {
      setError(r.error);
    }
  };

  const handleTemplateRemove = async (type) => {
    if (!window.confirm(`Supprimer le modèle ${type} ? Les PDF intégrés seront utilisés à la place.`)) return;
    const r = await TemplateService.remove(userId, type);
    if (r.success) {
      setTemplates(await TemplateService.getAll(userId));
      AuditService.log(AUDIT_ACTIONS.PROFILE_UPDATE, { entityType: 'modele', label: `Modèle ${type} supprimé` });
    } else setError(r.error);
  };

  const downloadTemplate = (t) => {
    const bytes = Uint8Array.from(atob(t.data), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
    const a = document.createElement('a'); a.href = url; a.download = t.name || `${t.type}.docx`; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const copyTag = async (tag) => {
    const text = tag === 'lignes' ? '{#lignes}{{designation}} — {{quantite}} × {{prix_unitaire}} = {{total}}{/lignes}' : `{{${tag}}}`;
    try { await navigator.clipboard.writeText(text); setCopiedTag(tag); setTimeout(() => setCopiedTag(''), 1500); } catch (e) { /* presse-papiers indisponible */ }
  };

  // Agents présents dans la liste de noms mais sans compte
  const legacyAgents = agents.filter(name => !members.some(m => m.agentName === name));

  if (loading) {
    return (
      <div className="modal-scrim" style={{ zIndex: 9999 }}>
        <div className="modal-panel max-w-xs p-8">
          <div className="flex items-center">
            <svg className="animate-spin h-6 w-6 mr-3 text-brand-700" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Chargement...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-scrim" style={{ zIndex: 9999 }}>
      <div className="modal-panel max-w-2xl">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center">
            <Building className="h-5 w-5 mr-2 text-brand-700" />
            Profil Cabinet
          </h2>
          <button onClick={onClose} className="icon-btn" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-4 sm:px-6 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'info'
                ? 'border-brand-700 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Informations
          </button>
          <button
            onClick={() => setActiveTab('agents')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'agents'
                ? 'border-brand-700 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Agents ({agents.length})
          </button>
          <button
            onClick={() => setActiveTab('projectTypes')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'projectTypes'
                ? 'border-brand-700 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Types de projet ({projectTypes.length})
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'templates'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Modèles
          </button>
        </div>

        <div className="p-5 sm:p-6 overflow-y-auto custom-scrollbar flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {success}
            </div>
          )}

          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Logo Upload */}
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center overflow-hidden bg-slate-50">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Building className="h-10 w-10 text-slate-300" />
                    )}
                  </div>
                  <label className="mt-2 flex items-center justify-center px-3 py-1.5 bg-brand-50 text-brand-700 text-xs font-medium rounded-lg cursor-pointer hover:bg-brand-100 transition-colors">
                    <Upload className="h-3 w-3 mr-1" />
                    Logo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Nom d'architecte <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={profile.nomArchitecte}
                      onChange={(e) => handleProfileChange('nomArchitecte', e.target.value)}
                      className="field"
                      placeholder="Nom de l'architecte"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Nom de la société <span className="text-slate-400">(Optionnel)</span>
                    </label>
                    <input
                      type="text"
                      value={profile.nomSociete}
                      onChange={(e) => handleProfileChange('nomSociete', e.target.value)}
                      className="field"
                      placeholder="Nom de la société"
                    />
                  </div>
                </div>
              </div>

              {/* Identifiants fiscaux */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Identifiants Fiscaux</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">ICE</label>
                    <input
                      type="text"
                      value={profile.ice}
                      onChange={(e) => handleProfileChange('ice', e.target.value)}
                      className="field"
                      placeholder="ICE"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Patente</label>
                    <input
                      type="text"
                      value={profile.patente}
                      onChange={(e) => handleProfileChange('patente', e.target.value)}
                      className="field"
                      placeholder="Patente"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">RC</label>
                    <input
                      type="text"
                      value={profile.rc}
                      onChange={(e) => handleProfileChange('rc', e.target.value)}
                      className="field"
                      placeholder="RC"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">IF</label>
                    <input
                      type="text"
                      value={profile.identifiantFiscal}
                      onChange={(e) => handleProfileChange('identifiantFiscal', e.target.value)}
                      className="field"
                      placeholder="Identifiant Fiscal"
                    />
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Siège social</label>
                <input
                  type="text"
                  value={profile.siegeSocial}
                  onChange={(e) => handleProfileChange('siegeSocial', e.target.value)}
                  className="field"
                  placeholder="Adresse du siège social"
                />
              </div>

              {/* Informations bancaires */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">
                  Informations Bancaires <span className="text-slate-400 font-normal">(Optionnel)</span>
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">RIB</label>
                    <input
                      type="text"
                      value={profile.rib}
                      onChange={(e) => handleProfileChange('rib', e.target.value)}
                      className="field"
                      placeholder="RIB"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Banque</label>
                    <input
                      type="text"
                      value={profile.banque}
                      onChange={(e) => handleProfileChange('banque', e.target.value)}
                      className="field"
                      placeholder="Nom de la banque"
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Contact</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                      className="field"
                      placeholder="email@exemple.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Mobile <span className="text-slate-400">(Optionnel)</span>
                    </label>
                    <input
                      type="tel"
                      value={profile.mobile}
                      onChange={(e) => handleProfileChange('mobile', e.target.value)}
                      className="field"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Fixe <span className="text-slate-400">(Optionnel)</span>
                    </label>
                    <input
                      type="tel"
                      value={profile.fixe}
                      onChange={(e) => handleProfileChange('fixe', e.target.value)}
                      className="field"
                      placeholder="05 XX XX XX XX"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Fax <span className="text-slate-400">(Optionnel)</span>
                    </label>
                    <input
                      type="tel"
                      value={profile.fax}
                      onChange={(e) => handleProfileChange('fax', e.target.value)}
                      className="field"
                      placeholder="05 XX XX XX XX"
                    />
                  </div>
                </div>
              </div>

              {/* Gestion & notifications */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Gestion & notifications</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="label">Délai de retard (jours)</label>
                    <input
                      type="number" min="1" inputMode="numeric"
                      value={profile.delaiRetardJours}
                      onChange={(e) => handleProfileChange('delaiRetardJours', e.target.value)}
                      className="field"
                    />
                    <p className="mt-1 text-xs text-slate-500">Un dossier sans évolution de statut au-delà de ce délai est signalé « en retard ».</p>
                  </div>
                  <div>
                    <label className="label">TVA par défaut (%)</label>
                    <input
                      type="number" min="0" inputMode="decimal"
                      value={profile.tauxTVA}
                      onChange={(e) => handleProfileChange('tauxTVA', e.target.value)}
                      className="field"
                    />
                    <p className="mt-1 text-xs text-slate-500">Utilisée pour les nouveaux devis et factures.</p>
                  </div>
                  <div>
                    <label className="label">Webhook notifications (n8n)</label>
                    <input
                      type="url"
                      value={profile.webhookUrl}
                      onChange={(e) => handleProfileChange('webhookUrl', e.target.value)}
                      className="field"
                      placeholder="https://…/webhook/…"
                      disabled={!isOwner}
                      title={isOwner ? '' : 'Modifiable uniquement par le propriétaire du cabinet'}
                    />
                    <div className="flex items-center gap-2 mt-1">
                      <button
                        type="button"
                        onClick={async () => {
                          NotifyService.configure({ url: profile.webhookUrl, cabinet: profile.nomSociete || profile.nomArchitecte });
                          if (!NotifyService.isConfigured()) { setWebhookTest('URL invalide'); return; }
                          await NotifyService.send('test', { message: 'Test depuis Suivi des Affaires' });
                          setWebhookTest('Envoyé (vérifiez n8n)');
                        }}
                        className="btn-ghost !min-h-[32px] px-2 text-xs text-brand-700"
                      >
                        <Bell className="h-3.5 w-3.5" /> Tester
                      </button>
                      <span className="text-xs text-slate-500">{webhookTest}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">Reçoit un JSON à chaque changement de statut, avance, dépense ou mention.</p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn-primary w-full"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Sauvegarder le profil
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'agents' && (
            <div className="space-y-6">
              {/* Créer un compte agent */}
              <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-brand-800 mb-3">Nouvel agent — création de compte</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={memberForm.agentName}
                      onChange={(e) => handleMemberFormChange('agentName', e.target.value)}
                      placeholder="Nom de l'agent *"
                      className="field pl-9"
                    />
                  </div>
                  <select
                    value={memberForm.role}
                    onChange={(e) => handleMemberFormChange('role', e.target.value)}
                    className="field"
                  >
                    <option value={ROLES.TECHNICIEN}>Technicien — ses dossiers, sans comptabilité</option>
                    <option value={ROLES.SECRETAIRE}>Secrétaire — accès complet</option>
                    <option value={ROLES.ARCHITECT}>Architecte — accès complet</option>
                  </select>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="email"
                      value={memberForm.email}
                      onChange={(e) => handleMemberFormChange('email', e.target.value)}
                      placeholder="Email de connexion *"
                      autoComplete="off"
                      className="field pl-9"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="password"
                      value={memberForm.password}
                      onChange={(e) => handleMemberFormChange('password', e.target.value)}
                      placeholder="Mot de passe (6 car. min) *"
                      autoComplete="new-password"
                      className="field pl-9"
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateMember()}
                    />
                  </div>
                </div>
                <button
                  onClick={handleCreateMember}
                  disabled={creatingMember}
                  className="btn-primary w-full mt-3"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {creatingMember ? 'Création du compte...' : 'Créer le compte'}
                </button>
                <p className="mt-2 text-xs text-brand-700">
                  L'agent se connectera avec cet email et ce mot de passe. Les techniciens ne voient que
                  les dossiers dont ils sont « Agent Responsable », sans les informations financières.
                </p>
              </div>

              {/* Liste des membres */}
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {members.length === 0 && legacyAgents.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Aucun agent ajouté</p>
                ) : (
                  <>
                    {members.map((member) => {
                      const inactive = member.isActive === false;
                      return (
                        <div
                          key={member.id}
                          className={`flex items-center justify-between px-4 py-3 rounded-lg ${inactive ? 'bg-slate-100 opacity-70' : 'bg-slate-50'}`}
                        >
                          <div className="flex items-center min-w-0">
                            <User className="h-4 w-4 text-slate-400 mr-3 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="flex items-center flex-wrap gap-2">
                                <span className={`text-sm font-medium ${inactive ? 'text-slate-500 line-through' : 'text-slate-700'}`}>
                                  {member.agentName}
                                </span>
                                {inactive ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-300 text-slate-700">Désactivé</span>
                                ) : (
                                  <select
                                    value={member.role}
                                    onChange={(e) => handleChangeMemberRole(member, e.target.value)}
                                    className={`text-xs px-2 py-0.5 rounded-full border-0 focus:ring-2 focus:ring-brand-500 ${
                                      member.role === ROLES.TECHNICIEN ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                                    }`}
                                    title="Changer le rôle"
                                  >
                                    {Object.values(ROLES).map(r => (
                                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 truncate">{member.email}</p>
                            </div>
                          </div>
                          {inactive ? (
                            <button
                              onClick={() => handleReactivateMember(member)}
                              className="icon-btn text-emerald-600 hover:bg-emerald-50"
                              title="Réactiver le compte"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeactivateMember(member)}
                              className="icon-btn text-red-500 hover:bg-red-50 hover:text-red-700"
                              title="Désactiver le compte"
                            >
                              <UserX className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {legacyAgents.map((agent) => (
                      <div
                        key={`legacy-${agent}`}
                        className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-slate-400 mr-3" />
                          <span className="text-sm font-medium text-slate-700">{agent}</span>
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Sans compte</span>
                        </div>
                        <button
                          onClick={() => handleDeleteLegacyAgent(agent)}
                          className="icon-btn text-red-500 hover:bg-red-50 hover:text-red-700"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'projectTypes' && (
            <div className="space-y-4">
              {/* Add new project type */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newProjectType}
                  onChange={(e) => setNewProjectType(e.target.value)}
                  placeholder="Nouveau type de projet"
                  className="field flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddProjectType()}
                />
                <button
                  onClick={handleAddProjectType}
                  className="btn-primary"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>

              {/* Project types list */}
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {projectTypes.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Aucun type de projet ajouté</p>
                ) : (
                  projectTypes.map((type, index) => (
                    <div
                      key={type}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg cursor-move transition-all ${
                        draggedTypeIndex === index ? 'opacity-50 ring-2 ring-brand-400' : ''
                      } ${draggedTypeIndex !== null && draggedTypeIndex !== index ? 'hover:bg-brand-50' : ''}`}
                    >
                      <div className="flex items-center">
                        <GripVertical className="h-4 w-4 text-slate-300 mr-2 cursor-grab active:cursor-grabbing" />
                        <FileText className="h-4 w-4 text-slate-400 mr-2" />
                        <span className="text-sm font-medium text-slate-700">{type}</span>
                      </div>
                      <button
                        onClick={() => handleDeleteProjectType(type)}
                        className="icon-btn text-red-500 hover:bg-red-50 hover:text-red-700"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Drag hint */}
              {projectTypes.length > 1 && (
                <p className="text-xs text-slate-400 text-center">
                  Glissez-déposez pour réorganiser
                </p>
              )}

              {/* Auto-save notice */}
              <div className="pt-4 border-t">
                <p className="text-xs text-green-600 text-center">
                  Les modifications sont sauvegardées automatiquement
                </p>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="space-y-5">
              <p className="text-sm text-slate-600">
                Déposez vos propres modèles <strong>Word (.docx)</strong> de devis et de facture. Les balises ci-dessous sont
                remplacées automatiquement à la génération (bouton « Word » dans Comptabilité → Devis & Factures).
                Sans modèle, le PDF intégré est utilisé. Taille max {Math.round(TEMPLATE_MAX_BYTES / 1024)} Ko, sans images lourdes.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TEMPLATE_TYPES.map((t) => {
                  const tpl = templates[t.key];
                  return (
                    <div key={t.key} className="card p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <FileType className="h-5 w-5 text-brand-700" />
                        <h4 className="font-semibold text-slate-900">Modèle {t.label}</h4>
                        <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full ${tpl ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{tpl ? 'Personnalisé' : 'PDF intégré'}</span>
                      </div>
                      {tpl && (
                        <p className="text-xs text-slate-500 truncate" title={tpl.name}>{tpl.name} · {Math.round((tpl.size || 0) / 1024)} Ko</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <label className="btn-primary cursor-pointer !min-h-[40px]">
                          <Upload className="h-4 w-4" />
                          {templateBusy === t.key ? 'Envoi…' : (tpl ? 'Remplacer' : 'Choisir un .docx')}
                          <input type="file" accept=".docx" className="hidden" disabled={templateBusy === t.key} onChange={(e) => { handleTemplateUpload(t.key, e.target.files?.[0]); e.target.value = ''; }} />
                        </label>
                        {tpl && (
                          <>
                            <button onClick={() => downloadTemplate(tpl)} className="btn-secondary !min-h-[40px]"><Download className="h-4 w-4" /> Télécharger</button>
                            <button onClick={() => handleTemplateRemove(t.key)} className="btn-danger-soft !min-h-[40px]"><Trash2 className="h-4 w-4" /></button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Balises disponibles (cliquer pour copier)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto custom-scrollbar pr-1">
                  {TEMPLATE_TAGS.map(([tag, desc]) => (
                    <button key={tag} onClick={() => copyTag(tag)} className="flex items-center gap-2 text-left px-3 py-2 rounded-lg bg-slate-50 hover:bg-brand-50 min-h-[40px]">
                      <code className="text-xs font-mono text-brand-700 whitespace-nowrap">{tag === 'lignes' ? '{#lignes}…{/lignes}' : `{{${tag}}}`}</code>
                      <span className="text-xs text-slate-500 truncate flex-1">{desc}</span>
                      <Copy className={`h-3.5 w-3.5 flex-shrink-0 ${copiedTag === tag ? 'text-emerald-600' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Dans Word, écrivez les balises telles quelles dans votre mise en page. Pour le tableau des lignes, placez
                  <code className="mx-1">{'{#lignes}'}</code> au début de la ligne du tableau et <code className="mx-1">{'{/lignes}'}</code> à la fin.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 sm:px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="btn-secondary w-full">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ user }) => {
  // Rôle et cabinet de l'utilisateur connecté
  const userData = user?.userData || {};
  const role = userData.role || ROLES.ARCHITECT;
  const cabinetId = userData.cabinetId || user?.uid;
  const isTech = role === ROLES.TECHNICIEN;
  const canManage = FULL_ACCESS_ROLES.includes(role) && !isTech;
  const canSeeFinance = canManage;
  // Administration du cabinet (statistiques, profil, journal) : architectes uniquement
  const canAdmin = canManage && role === ROLES.ARCHITECT;
  const agentName = userData.agentName || '';

  // États
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');
  const [sortConfig, setSortConfig] = useState({ field: 'createdAt', direction: 'desc' });
  const [showProfileManagement, setShowProfileManagement] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [clients, setClients] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState('');
  const [lateOnly, setLateOnly] = useState(false);
  const [mapFilters, setMapFilters] = useState({ statuts: [], nature: '', agent: '', commune: '' });
  const [showMapFilters, setShowMapFilters] = useState(false);
  const [cabinetMembers, setCabinetMembers] = useState([]);
  const delaiRetard = parseInt(userProfile?.delaiRetardJours, 10) || DEFAULT_DELAI_RETARD_JOURS;
  const authorInfo = useMemo(() => ({
    uid: user?.uid,
    name: userData.agentName || userData.username || user?.displayName || user?.email || '',
    role
  }), [user?.uid, user?.displayName, user?.email, userData.agentName, userData.username, role]);

  // Contexte d'audit + journal de connexion (une fois par session)
  useEffect(() => {
    if (!cabinetId || !user?.uid) return;
    AuditService.setContext({ cabinetId, userUid: user.uid, userName: authorInfo.name, role });
    const key = `audit_login_${user.uid}`;
    try {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        AuditService.log(AUDIT_ACTIONS.LOGIN, { entityType: 'session', label: user.email || '' });
      }
    } catch (e) { /* stockage indisponible */ }
  }, [cabinetId, user?.uid, user?.email, authorInfo.name, role]);

  // Membres du cabinet (accès complet) : résolution technicien → compte pour les notifications
  const loadCabinetMembers = useCallback(async () => {
    if (!cabinetId || !canManage) return;
    const r = await MemberService.getMembers(cabinetId);
    if (r.success) setCabinetMembers(r.members);
  }, [cabinetId, canManage]);
  useEffect(() => { loadCabinetMembers(); }, [loadCabinetMembers]);

  // Notifications in-app : contexte d'émission
  useEffect(() => {
    NotificationService.configure({ cabinetId, me: authorInfo, members: cabinetMembers, canFull: canManage });
  }, [cabinetId, authorInfo, cabinetMembers, canManage]);

  // Ouvrir un dossier depuis une notification (chargé si absent de la liste courante)
  const openProjectById = useCallback(async (projectId) => {
    const local = projects.find(p => p.id === projectId);
    if (local) { setSelectedProject(local); return; }
    const r = await ProjectService.getProject(projectId);
    if (r.success) setSelectedProject(r.project);
  }, [projects]);

  // Webhook de notification (profil cabinet)
  useEffect(() => {
    NotifyService.configure({ url: userProfile?.webhookUrl || '', cabinet: userProfile?.nomSociete || userProfile?.nomArchitecte || '' });
  }, [userProfile?.webhookUrl, userProfile?.nomSociete, userProfile?.nomArchitecte]);

  // Annuaire clients (accès complet uniquement)
  useEffect(() => {
    if (!cabinetId || !canManage) return undefined;
    return ClientService.subscribe(cabinetId, setClients);
  }, [cabinetId, canManage]);

  // Charger les projets
  useEffect(() => {
    if (!cabinetId) return;

    // Un technicien sans nom d'agent ne peut voir aucun dossier
    if (isTech && !agentName) {
      setProjects([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const unsubscribe = ProjectService.subscribeToUserProjects(cabinetId, (projectsData) => {
      setProjects(projectsData);
      setLoading(false);
    }, isTech ? { agentResponsable: agentName } : {});

    return () => unsubscribe();
  }, [cabinetId, isTech, agentName]);

  // Charger les agents et le profil
  useEffect(() => {
    if (!cabinetId) return;

    const unsubscribe = AgentService.subscribeToAgents(cabinetId, (agentsData) => {
      setAgents(agentsData);
    });

    // Charger le profil et les types de projet
    const loadProfileAndTypes = async () => {
      const result = await AgentService.getUserData(cabinetId);
      if (result.success && result.data) {
        if (result.data.profile) {
          setUserProfile(result.data.profile);
        }
        // Load project types or use defaults
        setProjectTypes(result.data.projectTypes || [...NATURES_PROJETS]);
      } else {
        // No data yet, use default project types
        setProjectTypes([...NATURES_PROJETS]);
      }
    };
    loadProfileAndTypes();

    return () => unsubscribe();
  }, [cabinetId]);

  // Recherche avec debounce
  const debouncedSearch = useMemo(
    () => debounce((term) => setSearchTerm(term), DEFAULT_CONFIG.DEBOUNCE_DELAY),
    []
  );

  // Projets filtrés et triés
  const filteredProjects = useMemo(() => {
    let result = searchProjects(projects, searchTerm);
    if (paymentFilter) result = result.filter(p => getPaymentStatus(p) === paymentFilter);
    if (lateOnly) result = result.filter(p => isProjectLate(p, delaiRetard));
    return sortProjects(result, sortConfig.field, sortConfig.direction);
  }, [projects, searchTerm, sortConfig, paymentFilter, lateOnly, delaiRetard]);

  const lateProjects = useMemo(() => projects.filter(p => isProjectLate(p, delaiRetard)), [projects, delaiRetard]);

  // Filtres de la carte (s'ajoutent à la recherche et aux filtres de la liste)
  const mapProjects = useMemo(() => filteredProjects.filter(p => {
    if (mapFilters.statuts.length && !mapFilters.statuts.includes(p.statut || 'En cours')) return false;
    if (mapFilters.nature && p.natureProjet !== mapFilters.nature) return false;
    if (mapFilters.agent && p.agentResponsable !== mapFilters.agent) return false;
    if (mapFilters.commune && (p.commune || '') !== mapFilters.commune) return false;
    return true;
  }), [filteredProjects, mapFilters]);
  const mapOptions = useMemo(() => ({
    natures: Array.from(new Set(projects.map(p => p.natureProjet).filter(Boolean))).sort(),
    agentsList: Array.from(new Set(projects.map(p => p.agentResponsable).filter(Boolean))).sort(),
    communes: Array.from(new Set(projects.map(p => p.commune).filter(Boolean))).sort()
  }), [projects]);
  const mapFilterCount = mapFilters.statuts.length + (mapFilters.nature ? 1 : 0) + (mapFilters.agent ? 1 : 0) + (mapFilters.commune ? 1 : 0);
  const geolocated = mapProjects.filter(p => p.coordonnees?.length).length;
  const toggleMapStatut = (st) => setMapFilters(f => ({ ...f, statuts: f.statuts.includes(st) ? f.statuts.filter(x => x !== st) : [...f.statuts, st] }));

  // Statistiques rapides
  const quickStats = useMemo(() => {
    const total = projects.length;
    const approved = projects.filter(p =>
      ['Favorable', 'Autorisé', 'Chantier démarré', 'Fin Travaux'].includes(p.statut)
    ).length;
    const inProgress = projects.filter(p => p.statut === 'En cours').length;
    const totalRevenue = projects.reduce((sum, p) =>
      sum + (parseFloat(p.montantTotal) || 0) + (parseFloat(p.fraisSupplementaires) || 0), 0
    );

    return { total, approved, inProgress, totalRevenue };
  }, [projects]);

  // Gérer le tri
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sélection
  const toggleSelectProject = (projectId) => {
    setSelectedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProjects.length === filteredProjects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(filteredProjects.map(p => p.id));
    }
  };

  // Supprimer sélection
  const handleDeleteSelected = async () => {
    if (!window.confirm(`Supprimer ${selectedProjects.length} projet(s)? Cette action est irréversible.`)) {
      return;
    }

    const labels = projects.filter(p => selectedProjects.includes(p.id)).map(p => p.refGlobal).join(', ');
    const result = await ProjectService.deleteProjects(selectedProjects);
    if (result.success) {
      AuditService.log(AUDIT_ACTIONS.PROJECT_DELETE, { entityType: 'dossier', label: `${selectedProjects.length} dossier(s) : ${labels}` });
      setSelectedProjects([]);
    }
  };

  const deleteOne = async (project) => {
    if (!window.confirm('Supprimer ce projet?')) return;
    const result = await ProjectService.deleteProject(project.id);
    if (result.success) {
      AuditService.log(AUDIT_ACTIONS.PROJECT_DELETE, { entityType: 'dossier', entityId: project.id, label: `${project.refGlobal || ''} · ${project.client || ''}` });
    }
  };

  // Sauvegarder projet
  const handleSaveProject = async (projectData) => {
    const userId = cabinetId;

    if (canManage && projectData.clients?.length) {
      ClientService.ensureClients(cabinetId, [...clients], projectData.clients).then((created) => {
        created.forEach(c => AuditService.log(AUDIT_ACTIONS.CLIENT_CREATE, { entityType: 'client', entityId: c.id, label: `${c.nom} (depuis dossier)` }));
      });
    }

    if (editingProject?.id) {
      // Mise à jour (changement de statut éventuel : historique + remarque)
      const { statutRemarque = '', ...updates } = projectData;
      const oldStatut = editingProject.statut || 'En cours';
      const newStatut = updates.statut || oldStatut;
      const statusChanged = newStatut !== oldStatut;
      if (statusChanged) {
        updates.statusHistory = [...(editingProject.statusHistory || []), {
          status: newStatut, date: new Date().toISOString(), previousStatus: oldStatut, comment: statutRemarque
        }];
      }
      const result = await ProjectService.updateProject(editingProject.id, updates);
      if (result.success) {
        const merged = { ...editingProject, ...updates };
        AuditService.log(AUDIT_ACTIONS.PROJECT_UPDATE, { entityType: 'dossier', entityId: editingProject.id, label: `${editingProject.refGlobal || ''} · ${projectData.client || ''}` });
        if (statusChanged) {
          AuditService.log(AUDIT_ACTIONS.PROJECT_STATUS, { entityType: 'dossier', entityId: editingProject.id, label: `${editingProject.refGlobal || ''} · ${oldStatut} → ${newStatut}${statutRemarque ? ` (${statutRemarque})` : ''}` });
          NotifyService.send('statut', { dossier: merged.refGlobal, client: merged.client, ancien: oldStatut, nouveau: newStatut, commentaire: statutRemarque, agent: merged.agentResponsable, par: authorInfo.name });
          if (newStatut === 'Favorable' || newStatut === 'Défavorable') NotificationService.avis(merged, newStatut, statutRemarque);
        }
        if (updates.agentResponsable && updates.agentResponsable !== editingProject.agentResponsable) {
          NotificationService.dossierAssigned(merged);
        }
        if (selectedProject?.id === editingProject.id) setSelectedProject(merged);
        setShowProjectForm(false);
        setEditingProject(null);
      }
    } else {
      // Nouveau projet
      const refGlobal = generateRefGlobal(projects);
      const numeroDossier = generateNumeroDossier(projects);

      const { statutRemarque: _ignored, ...cleanData } = projectData;
      const newProject = {
        ...cleanData,
        userId,
        refGlobal,
        numeroDossier
      };

      const result = await ProjectService.addProject(newProject);
      if (result.success) {
        AuditService.log(AUDIT_ACTIONS.PROJECT_CREATE, { entityType: 'dossier', entityId: result.id, label: `${refGlobal} · ${projectData.client || ''}` });
        NotificationService.dossierNew({ ...newProject, id: result.id });
        setShowProjectForm(false);
      }
    }
  };

  // Nouveau type de projet saisi depuis le formulaire
  const handleAddProjectType = async (type) => {
    const clean = (type || '').trim();
    if (!clean || projectTypes.includes(clean)) return;
    const updated = [...projectTypes, clean];
    setProjectTypes(updated);
    await AgentService.saveUserData(cabinetId, { projectTypes: updated });
  };

  // Ouvrir dans Google Maps
  const openInGoogleMaps = (project) => {
    if (!project.coordonnees?.length || !project.zoneMerchich) return;

    const firstPoint = project.coordonnees[0];
    const wgs84 = merchichConverter.merchichToWGS84(firstPoint.x, firstPoint.y, project.zoneMerchich);

    if (wgs84) {
      window.open(`https://maps.google.com/maps?q=${wgs84.latitude},${wgs84.longitude}&hl=fr&z=15`, '_blank');
    }
  };

  // Déconnexion
  const handleLogout = async () => {
    await AuthService.signOut();
  };

  // Icône de tri
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronUp className="h-3 w-3 text-slate-300" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="h-3 w-3 text-brand-700" />
      : <ChevronDown className="h-3 w-3 text-brand-700" />;
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { key: 'dashboard', label: 'Tableau de bord', icon: Home, onClick: () => setActiveView('dashboard'), active: activeView === 'dashboard' },
    ...(canManage ? [
      { key: 'compta', label: 'Comptabilité', icon: Wallet, onClick: () => setActiveView('compta'), active: activeView === 'compta' },
      { key: 'clients', label: 'Clients', icon: Users, onClick: () => setActiveView('clients'), active: activeView === 'clients' },
    ] : []),
    ...(canAdmin ? [
      { key: 'statistics', label: 'Statistiques', icon: BarChart3, onClick: () => setActiveView('statistics'), active: activeView === 'statistics' },
      { key: 'journal', label: 'Journal', icon: History, onClick: () => setActiveView('journal'), active: activeView === 'journal' },
      { key: 'profile', label: 'Profil cabinet', icon: Building, onClick: () => setShowProfileManagement(true), active: false }
    ] : []),
    { key: 'account', label: 'Mon compte', icon: KeyRound, onClick: () => setShowAccountSettings(true), active: false }
  ];

  const cabinetLabel = userProfile?.nomSociete || userProfile?.nomArchitecte || user?.userData?.bureau || '';
  const displayName = userData.agentName || userData.username || user?.displayName || user?.email || '';
  const roleLabel = ROLE_LABELS[role] || 'Architecte';
  const initials = (displayName || '?').split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const pageTitles = { dashboard: 'Tableau de bord', statistics: 'Statistiques', compta: 'Comptabilité', clients: 'Clients', journal: "Journal d'activité" };
  const pageSubtitles = {
    statistics: "Vue d'ensemble de l'activité du cabinet",
    compta: 'Caisse, dépenses, devis et factures',
    clients: `${clients.length} client${clients.length > 1 ? 's' : ''} dans l'annuaire`,
    journal: 'Qui a fait quoi, et quand'
  };
  const pageTitle = pageTitles[activeView] || 'Tableau de bord';

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex lg:flex-col w-64 xl:w-72 flex-shrink-0 bg-gradient-to-b from-brand-600 to-brand-800 text-white sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="bg-white/15 p-2 rounded-xl">
            <MapPin className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-base leading-tight">Suivi des Affaires</p>
            <p className="text-xs text-brand-100 truncate">{cabinetLabel || 'Cabinet d\'architecture'}</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1" aria-label="Navigation principale">
          {navItems.map(item => (
            <button
              key={item.key}
              onClick={item.onClick}
              className={item.active ? 'nav-item-active' : 'nav-item-idle'}
              aria-current={item.active ? 'page' : undefined}
            >
              <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
              <span className="truncate">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-accent-500 text-slate-900 font-bold text-sm flex items-center justify-center flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{displayName}</p>
              <p className="text-xs text-brand-100 truncate">{roleLabel}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="nav-item-idle mt-1 text-red-200 hover:text-white hover:bg-red-500/30"
          >
            <LogOut className="h-[18px] w-[18px]" />
            Déconnexion
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
      {/* Top bar */}
      <header className="bg-white/90 backdrop-blur border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* Mobile brand */}
            <div className="flex items-center gap-3 lg:hidden min-w-0">
              <div className="bg-brand-600 p-2 rounded-xl flex-shrink-0">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base font-bold text-slate-900 leading-tight truncate">Suivi des Affaires</h1>
                <p className="text-xs text-slate-500 truncate">{cabinetLabel || user?.email}</p>
              </div>
            </div>

            {/* Desktop title */}
            <div className="hidden lg:block min-w-0">
              <h1 className="text-xl font-bold text-slate-900 leading-tight">{pageTitle}</h1>
              <p className="text-sm text-slate-500 truncate">
                {pageSubtitles[activeView] || `${projects.length} affaire${projects.length > 1 ? 's' : ''} suivie${projects.length > 1 ? 's' : ''}${lateProjects.length ? ` · ${lateProjects.length} en retard` : ''}`}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <NotificationBell cabinetId={cabinetId} uid={user?.uid} isArchitect={canAdmin} onOpenProject={openProjectById} />
              {(isTech || role === ROLES.SECRETAIRE) && (
                <span className={`hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${isTech ? 'bg-accent-100 text-accent-700' : 'bg-emerald-100 text-emerald-800'}`}>
                  {roleLabel}{isTech && agentName ? ` · ${agentName}` : ''}
                </span>
              )}
              {canManage && activeView === 'dashboard' && (
                <button
                  onClick={() => { setEditingProject(null); setShowProjectForm(true); }}
                  className="btn-primary hidden lg:inline-flex"
                >
                  <Plus className="h-4 w-4" />
                  Nouvelle affaire
                </button>
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="icon-btn lg:hidden"
                aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden mt-3 pt-3 border-t border-slate-200 space-y-1" aria-label="Navigation">
              {navItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => { item.onClick(); setMobileMenuOpen(false); }}
                  className={`flex items-center gap-3 w-full px-3 py-3 rounded-xl text-sm font-medium transition-colors min-h-[44px] ${
                    item.active ? 'bg-brand-50 text-brand-800' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="h-[18px] w-[18px]" />
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              ))}
              <div className="flex items-center gap-3 px-3 py-2 mt-2 border-t border-slate-200 pt-3">
                <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-800 font-bold text-xs flex items-center justify-center">{initials}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{displayName}</p>
                  <p className="text-xs text-slate-500">{roleLabel}</p>
                </div>
                <button onClick={handleLogout} className="btn-danger-soft !min-h-[40px] px-3">
                  <LogOut className="h-4 w-4" />
                  Quitter
                </button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {activeView === 'dashboard' ? (
          <>
            {/* Carte + filtres */}
            <div className="mb-4 sm:mb-6">
              <div className="card mb-2 overflow-hidden">
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2">
                  <button
                    onClick={() => setShowMapFilters(v => !v)}
                    className="btn-ghost !min-h-[36px] px-2 text-slate-700"
                    aria-expanded={showMapFilters}
                  >
                    <Filter className="h-4 w-4" />
                    Filtres carte
                    {mapFilterCount > 0 && <span className="ml-1 bg-brand-600 text-white text-[11px] rounded-full px-1.5 py-0.5">{mapFilterCount}</span>}
                  </button>
                  <span className="text-xs text-slate-500 ml-auto">
                    {geolocated} sur {mapProjects.length} affaire{mapProjects.length > 1 ? 's' : ''} géolocalisée{geolocated > 1 ? 's' : ''}
                  </span>
                  {mapFilterCount > 0 && (
                    <button onClick={() => setMapFilters({ statuts: [], nature: '', agent: '', commune: '' })} className="btn-ghost !min-h-[36px] px-2 text-xs text-red-600">
                      <MapPinOff className="h-3.5 w-3.5" /> Réinitialiser
                    </button>
                  )}
                </div>
                {showMapFilters && (
                  <div className="px-3 sm:px-4 pb-3 border-t border-slate-100 pt-3 space-y-3">
                    <div className="flex flex-wrap gap-1.5">
                      {STATUS_ORDER.map(st => {
                        const on = mapFilters.statuts.includes(st);
                        const color = STATUS_COLORS[st];
                        return (
                          <button
                            key={st}
                            onClick={() => toggleMapStatut(st)}
                            className={`inline-flex items-center gap-1.5 px-2.5 min-h-[32px] rounded-full text-xs font-medium border transition-colors ${on ? 'text-white border-transparent' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                            style={on ? { backgroundColor: color } : undefined}
                            aria-pressed={on}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: on ? '#fff' : color }} />
                            {st}
                          </button>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <select value={mapFilters.nature} onChange={(e) => setMapFilters(f => ({ ...f, nature: e.target.value }))} className="field !min-h-[40px]" aria-label="Nature du projet">
                        <option value="">Toutes natures</option>
                        {mapOptions.natures.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <select value={mapFilters.agent} onChange={(e) => setMapFilters(f => ({ ...f, agent: e.target.value }))} className="field !min-h-[40px]" aria-label="Agent responsable">
                        <option value="">Tous agents</option>
                        {mapOptions.agentsList.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <select value={mapFilters.commune} onChange={(e) => setMapFilters(f => ({ ...f, commune: e.target.value }))} className="field !min-h-[40px]" aria-label="Commune">
                        <option value="">Toutes communes</option>
                        {mapOptions.communes.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <Map
                projects={mapProjects}
                onProjectSelect={(project) => setSelectedProject(project)}
                canSeeFinance={canSeeFinance}
                height="50vh"
                selectedProjectId={selectedProject?.id}
              />
            </div>

            {/* Barre de recherche et actions */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 sm:mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  placeholder="Rechercher un client, une référence, une commune…"
                  aria-label="Rechercher"
                  onChange={(e) => debouncedSearch(e.target.value)}
                  className="field pl-10"
                />
              </div>

              {canManage && (
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                {selectedProjects.length > 0 && (
                  <button onClick={handleDeleteSelected} className="btn-danger-soft">
                    <Trash2 className="h-4 w-4" />
                    Supprimer ({selectedProjects.length})
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditingProject(null);
                    setShowProjectForm(true);
                  }}
                  className="btn-primary lg:hidden flex-1 sm:flex-none"
                >
                  <Plus className="h-4 w-4" />
                  Nouvelle affaire
                </button>
              </div>
              )}
            </div>

            {/* Filtres */}
            {(canSeeFinance || lateOnly) && (
              <div className="flex flex-wrap items-center gap-2 mb-4 -mt-2">
                {canSeeFinance && (
                  <>
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 mr-1">Paiement</span>
                    {[['', 'Tous'], ['non_paye', 'Non payé'], ['partiel', 'Partiel'], ['solde', 'Soldé']].map(([v, l]) => (
                      <button
                        key={v}
                        onClick={() => setPaymentFilter(v)}
                        className={`px-3 min-h-[36px] rounded-full text-xs font-medium transition-colors ${
                          paymentFilter === v ? 'bg-brand-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </>
                )}
                {lateOnly && (
                  <button onClick={() => setLateOnly(false)} className="px-3 min-h-[36px] rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> En retard uniquement · retirer
                  </button>
                )}
              </div>
            )}

            {/* Statistiques rapides */}
            <div className={`grid gap-3 sm:gap-4 mb-4 sm:mb-6 ${canSeeFinance ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'}`}>
              {[
                { label: 'Total', value: quickStats.total, icon: FileText, tone: 'bg-brand-50 text-brand-700' },
                { label: 'Approuvés', value: quickStats.approved, icon: CheckCircle, tone: 'bg-emerald-50 text-emerald-700' },
                { label: 'En cours', value: quickStats.inProgress, icon: Clock, tone: 'bg-accent-50 text-accent-700' },
                { label: 'En retard', value: lateProjects.length, icon: AlertTriangle, tone: lateProjects.length ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500', onClick: () => setLateOnly(v => !v), active: lateOnly },
                ...(canSeeFinance ? [{ label: 'Revenus', value: `${formatNumber(quickStats.totalRevenue)} DH`, icon: DollarSign, tone: 'bg-violet-50 text-violet-700', money: true }] : [])
              ].map((kpi) => (
                <div
                  key={kpi.label}
                  onClick={kpi.onClick}
                  role={kpi.onClick ? 'button' : undefined}
                  tabIndex={kpi.onClick ? 0 : undefined}
                  onKeyDown={kpi.onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); kpi.onClick(); } } : undefined}
                  className={`card p-4 sm:p-5 flex items-center gap-3 sm:gap-4 transition-shadow duration-200 hover:shadow-card-hover ${kpi.money ? 'col-span-2 lg:col-span-1' : ''} ${kpi.onClick ? 'cursor-pointer' : ''} ${kpi.active ? 'ring-2 ring-red-400' : ''}`}
                >
                  <div className={`p-2.5 sm:p-3 rounded-xl flex-shrink-0 ${kpi.tone}`}>
                    <kpi.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500">{kpi.label}</p>
                    <p className={`${kpi.money ? 'text-lg sm:text-2xl' : ''} kpi-value mt-1 truncate`}>{kpi.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Tableau des projets */}
            <div className="card overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-48 sm:h-64">
                  <div className="flex items-center text-slate-500">
                    <svg className="animate-spin h-5 w-5 sm:h-6 sm:w-6 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Chargement...</span>
                  </div>
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 sm:h-64 text-slate-500 px-4">
                  <div className="p-4 rounded-2xl bg-slate-100 mb-3">
                    <FileText className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-base sm:text-lg font-medium text-slate-700">Aucun projet trouvé</p>
                  <p className="text-xs sm:text-sm text-center">
                    {isTech ? 'Aucun dossier ne vous est assigné pour le moment' : 'Créez votre première affaire pour commencer'}
                  </p>
                </div>
              ) : (
                <>
                {/* Mobile Card View */}
                <div className="sm:hidden divide-y divide-slate-100">
                  {filteredProjects.map((project) => {
                    const resteAPayer = calculateResteAPayer(
                      project.montantTotal,
                      project.avances,
                      project.fraisSupplementaires
                    );
                    const statut = project.statut || 'En cours';

                    return (
                      <div
                        key={project.id}
                        className={`p-4 ${selectedProjects.includes(project.id) ? 'bg-brand-50/70' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center">
                            {canManage && (
                              <input
                                type="checkbox"
                                checked={selectedProjects.includes(project.id)}
                                onChange={() => toggleSelectProject(project.id)}
                                className="h-4 w-4 text-brand-700 rounded border-slate-300 mr-2"
                              />
                            )}
                            <button
                              onClick={() => setSelectedProject(project)}
                              className="font-semibold text-slate-900 text-base text-left hover:text-brand-700 py-1.5 min-h-[36px]"
                            >
                              {project.client || '-'}
                            </button>
                          </div>
                          <StatusBadge status={statut} size="xs" />
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 mb-2">
                          <div>Réf: <span className="text-slate-900">{project.refGlobal || '-'}</span>
                            {isProjectLate(project, delaiRetard) && <span className="ml-1 text-red-700 font-medium">· retard {getDaysSinceStatus(project)} j</span>}
                          </div>
                          <div>Commune: <span className="text-slate-900">{project.commune || '-'}</span></div>
                          {canSeeFinance ? (
                            <>
                              <div>Montant: <span className="font-medium text-slate-900">{formatNumber(project.montantTotal || 0)} DH</span></div>
                              <div>Reste: <span className={`font-medium ${resteAPayer > 0 ? 'text-orange-600' : 'text-green-600'}`}>{formatNumber(resteAPayer)} DH</span></div>
                              <div className="col-span-2"><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${PAYMENT_STATUS_STYLES[getPaymentStatus(project)]}`}>{PAYMENT_STATUS_LABELS[getPaymentStatus(project)]}</span></div>
                            </>
                          ) : (
                            <>
                              <div>Nature: <span className="text-slate-900">{project.natureProjet || '-'}</span></div>
                              <div>Agent: <span className="text-slate-900">{project.agentResponsable || '-'}</span></div>
                            </>
                          )}
                        </div>
                        <div className="flex items-center justify-end space-x-2 pt-2 border-t border-slate-100">
                          {canManage ? (
                            <button
                              onClick={() => {
                                setEditingProject(project);
                                setShowProjectForm(true);
                              }}
                              className="icon-btn hover:text-brand-700 hover:bg-brand-50"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => setSelectedProject(project)}
                              className="icon-btn hover:text-brand-700 hover:bg-brand-50"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openInGoogleMaps(project)}
                            disabled={!project.coordonnees?.length}
                            className="icon-btn hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-30"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                          {canManage && (
                            <button
                              onClick={() => deleteOne(project)}
                              className="icon-btn hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50/80 border-b border-slate-200">
                      <tr>
                        {canManage && (
                          <th className="px-4 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={selectedProjects.length === filteredProjects.length && filteredProjects.length > 0}
                              onChange={toggleSelectAll}
                              className="h-4 w-4 text-brand-700 rounded border-slate-300 focus:ring-brand-500"
                            />
                          </th>
                        )}
                        <th
                          className="table-head cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => handleSort('refGlobal')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Réf Global</span>
                            <SortIcon field="refGlobal" />
                          </div>
                        </th>
                        <th
                          className="table-head cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => handleSort('client')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Client</span>
                            <SortIcon field="client" />
                          </div>
                        </th>
                        <th
                          className="table-head cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => handleSort('commune')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Commune</span>
                            <SortIcon field="commune" />
                          </div>
                        </th>
                        <th className="table-head">
                          Régime
                        </th>
                        <th className="table-head">
                          Statut
                        </th>
                        {canSeeFinance ? (
                          <>
                            <th
                              className="table-head !text-right cursor-pointer hover:bg-slate-100 transition-colors"
                              onClick={() => handleSort('montantTotal')}
                            >
                              <div className="flex items-center justify-end space-x-1">
                                <span>Montant</span>
                                <SortIcon field="montantTotal" />
                              </div>
                            </th>
                            <th
                              className="table-head !text-right cursor-pointer hover:bg-slate-100 transition-colors"
                              onClick={() => handleSort('resteAPayer')}
                            >
                              <div className="flex items-center justify-end space-x-1">
                                <span>Reste</span>
                                <SortIcon field="resteAPayer" />
                              </div>
                            </th>
                            <th className="table-head">Paiement</th>
                          </>
                        ) : (
                          <>
                            <th className="table-head">
                              Nature
                            </th>
                            <th className="table-head">
                              Date
                            </th>
                          </>
                        )}
                        <th className="table-head !text-center">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredProjects.map((project) => {
                        const resteAPayer = calculateResteAPayer(
                          project.montantTotal,
                          project.avances,
                          project.fraisSupplementaires
                        );
                        const statut = project.statut || 'En cours';
    
                        return (
                          <tr
                            key={project.id}
                            className={`transition-colors duration-150 hover:bg-brand-50/40 ${
                              selectedProjects.includes(project.id) ? 'bg-brand-50/70' : ''
                            }`}
                          >
                            {canManage && (
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.includes(project.id)}
                                  onChange={() => toggleSelectProject(project.id)}
                                  className="h-4 w-4 text-brand-700 rounded border-slate-300 focus:ring-brand-500"
                                />
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">
                              {project.refGlobal || '-'}
                              {isProjectLate(project, delaiRetard) && (
                                <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full" title={`Aucune évolution depuis ${getDaysSinceStatus(project)} jours`}>
                                  <AlertTriangle className="h-3 w-3" /> {getDaysSinceStatus(project)} j
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-900">
                              <button
                                onClick={() => setSelectedProject(project)}
                                className="hover:text-brand-700 hover:underline text-left"
                              >
                                {project.client || '-'}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {project.commune || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {formatRegimeDisplay(project.regimeFoncier, project.regimeReference)}
                            </td>
                            <td className="px-4 py-3">
                              <StatusBadge status={statut} />
                            </td>
                            {canSeeFinance ? (
                              <>
                                <td className="px-4 py-3 text-sm text-slate-900 text-right font-semibold tabular-nums">
                                  {formatNumber(project.montantTotal || 0)} DH
                                </td>
                                <td className={`px-4 py-3 text-sm text-right font-semibold tabular-nums ${
                                  resteAPayer > 0 ? 'text-orange-600' : 'text-green-600'
                                }`}>
                                  {formatNumber(resteAPayer)} DH
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${PAYMENT_STATUS_STYLES[getPaymentStatus(project)]}`}>
                                    {PAYMENT_STATUS_LABELS[getPaymentStatus(project)]}
                                  </span>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3 text-sm text-slate-600">{project.natureProjet || '-'}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">{project.date || '-'}</td>
                              </>
                            )}
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center space-x-1">
                                {canManage ? (
                                  <button
                                    onClick={() => {
                                      setEditingProject(project);
                                      setShowProjectForm(true);
                                    }}
                                    className="icon-btn hover:text-brand-700 hover:bg-brand-50"
                                    title="Modifier"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setSelectedProject(project)}
                                    className="icon-btn hover:text-brand-700 hover:bg-brand-50"
                                    title="Consulter"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => openInGoogleMaps(project)}
                                  disabled={!project.coordonnees?.length}
                                  className="icon-btn hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-30"
                                  title="Voir sur Maps"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </button>
                                {canManage && (
                                  <button
                                    onClick={() => deleteOne(project)}
                                    className="icon-btn hover:text-red-600 hover:bg-red-50"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          </>
        ) : activeView === 'compta' && canManage ? (
          <Comptabilite cabinetId={cabinetId} projects={projects} user={authorInfo} profile={userProfile || {}} />
        ) : activeView === 'clients' && canManage ? (
          <ClientsList cabinetId={cabinetId} clients={clients} projects={projects} onOpenProject={(p) => setSelectedProject(p)} />
        ) : activeView === 'journal' && canAdmin ? (
          <AuditLog cabinetId={cabinetId} />
        ) : (
          canAdmin ? <Statistics userId={cabinetId} /> : null
        )}
      </main>

      {/* Footer */}
      <footer className="py-5 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs text-slate-400">
          <p>Application développée par METATOPO ·{' '}
            <a href="mailto:Bouhabba.igt@gmail.com" className="text-slate-500 hover:text-brand-700 transition-colors">Bouhabba.igt@gmail.com</a>
            {' · '}
            <a href="tel:+212661245376" className="text-slate-500 hover:text-brand-700 transition-colors">+212 661 245 376</a>
          </p>
        </div>
      </footer>
      </div>

      {/* Modals */}
      {showProjectForm && canManage && (
        <ProjectForm
          project={editingProject}
          onSave={handleSaveProject}
          onCancel={() => {
            setShowProjectForm(false);
            setEditingProject(null);
          }}
          existingProjects={projects}
          agents={agents}
          projectTypes={projectTypes}
          onAddProjectType={handleAddProjectType}
          clients={clients}
        />
      )}

      {showAccountSettings && (
        <AccountSettings
          user={user}
          role={role}
          onClose={() => setShowAccountSettings(false)}
        />
      )}

      {selectedProject && (
        <ProjectDetails
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onUpdate={(updatedProject) => {
            setSelectedProject(updatedProject);
          }}
          onDelete={() => {
            setSelectedProject(null);
          }}
          userId={cabinetId}
          readOnly={!canManage}
          canSeeFinance={canSeeFinance}
          canManage={canManage}
          agents={agents}
          author={authorInfo}
          delaiRetard={delaiRetard}
        />
      )}

      {showProfileManagement && canAdmin && (
        <ProfileManagement
          userId={cabinetId}
          isOwner={!userData.cabinetId}
          onClose={() => {
            setShowProfileManagement(false);
            loadCabinetMembers();
            // Reload profile and project types after closing
            AgentService.getUserData(cabinetId).then(result => {
              if (result.success && result.data) {
                if (result.data.profile) {
                  setUserProfile(result.data.profile);
                }
                setProjectTypes(result.data.projectTypes || [...NATURES_PROJETS]);
              }
            });
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
